/**
 * ExistingDocumentPicker - Reuse existing cropped documents from My Documents
 * 
 * Purpose: Allow users to select and reuse already-uploaded cropped documents
 * instead of uploading duplicates. This saves storage and speeds up the attachment flow.
 * 
 * Features:
 * - Search for existing documents by attachment role
 * - Show thumbnail preview, status badge, and metadata
 * - "Use This Document" / "Preview" / "Upload Different" actions
 * - Data isolation (users only see their own documents)
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import {
  Check, Eye, Upload, FileText, Calendar, Image,
  Loader2, X, RefreshCw, ChevronRight, Home, User, Shield, AlertTriangle
} from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import { toast } from 'sonner';
import {
  DOCUMENTS_API,
  attachmentRoleToDocumentCategory,
  preparedDocumentToPackageAttachment,
  resolvePreparedDocumentUrl,
} from '../../utils/supportingDocumentApi';
import { findUserDataBreachProofInLibrary } from '../../utils/disputeEnvelopeAttachments';

// Status badge styling
const STATUS_BADGES = {
  approved: { label: 'Approved', className: 'bg-emerald-900/40 text-emerald-300 border border-emerald-600/30' },
  verified: { label: 'Verified', className: 'bg-emerald-900/50 text-emerald-200 border border-emerald-500/30' },
  cropped_draft: { label: 'Cropped Draft', className: 'bg-amber-900/30 text-amber-300 border border-amber-700/30' },
};

// Document type icons
const DOC_TYPE_ICONS = {
  proof_of_address: Home,
  government_id: User,
  ssn_card: Shield,
  data_breach_proof: AlertTriangle,
  investigation_results: FileText,
};

// Attachment role labels
const ROLE_LABELS = {
  proof_of_address: 'Proof of Address',
  government_id: 'Government ID',
  ssn_card: 'SSN Card',
  data_breach_proof: 'Data Breach Proof',
  investigation_results: 'Investigation Results',
};

/**
 * Main component to pick existing documents
 */
const ExistingDocumentPicker = ({
  attachmentRole,  // 'proof_of_address', 'government_id', etc.
  packageId,
  onSelect,        // Called when user selects a document
  onUploadNew,     // Called when user wants to upload a new document instead
  onClose,
  isOpen = false,
}) => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Fetch existing documents for this attachment role
  useEffect(() => {
    if (isOpen && attachmentRole) {
      fetchExistingDocuments();
    }
  }, [isOpen, attachmentRole]);

  const fetchExistingDocuments = async () => {
    setLoading(true);
    try {
      const listParams =
        attachmentRole === 'data_breach_proof' || attachmentRole === 'investigation_results'
          ? { limit: 50 }
          : {
              category: attachmentRoleToDocumentCategory(attachmentRole),
              limit: 10,
            };

      const response = await axios.get(`${DOCUMENTS_API}/documents/list`, {
        params: listParams,
        headers: { Authorization: `Bearer ${token}` },
      });
      let items = response.data.items || [];
      if (attachmentRole === 'data_breach_proof') {
        const match = findUserDataBreachProofInLibrary(items);
        items = match ? [match, ...items.filter((d) => d.document_id !== match.document_id)] : items;
      }
      setDocuments(
        items.map((doc) => ({
          ...doc,
          cropped_url: resolvePreparedDocumentUrl(doc),
          file_url: resolvePreparedDocumentUrl(doc),
          preview_url: resolvePreparedDocumentUrl(doc),
          thumbnail_url: resolvePreparedDocumentUrl(doc),
        }))
      );
    } catch (error) {
      console.error('Error fetching existing documents:', error);
      toast.error('Failed to load existing documents');
    } finally {
      setLoading(false);
    }
  };

  const handleUseDocument = async (doc) => {
    if (!doc) return;

    setSubmitting(true);
    try {
      const attached = preparedDocumentToPackageAttachment(doc, attachmentRole);
      toast.success('Document attached successfully');
      onSelect?.(attached);
      onClose?.();
    } catch (error) {
      console.error('Error using existing document:', error);
      toast.error(error.response?.data?.detail || 'Failed to attach document');
    } finally {
      setSubmitting(false);
    }
  };

  const RoleIcon = DOC_TYPE_ICONS[attachmentRole] || FileText;
  const roleLabel = ROLE_LABELS[attachmentRole] || attachmentRole?.replace(/_/g, ' ');

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose?.()}>
      <DialogContent className="max-w-2xl bg-[#041610] border border-emerald-900/30 text-[#e8fef1]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#e8fef1]">
            <RoleIcon className="w-5 h-5 text-emerald-400" />
            Select Existing {roleLabel}
          </DialogTitle>
          <DialogDescription className="text-[#7fb89a]">
            Choose from your existing cropped documents to avoid uploading duplicates.
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-emerald-400 animate-spin mb-3" />
              <p className="text-sm text-[#7fb89a]">Loading your documents...</p>
            </div>
          ) : documents.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="w-16 h-16 rounded-full bg-emerald-900/20 flex items-center justify-center mb-4">
                <FileText className="w-8 h-8 text-emerald-500/50" />
              </div>
              <h3 className="font-medium text-[#e8fef1] mb-2">No Existing Documents Found</h3>
              <p className="text-sm text-[#7fb89a] max-w-xs mb-4">
                You don't have any cropped {roleLabel.toLowerCase()} documents in My Documents yet.
              </p>
              <Button
                onClick={onUploadNew}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                <Upload className="w-4 h-4 mr-2" />
                Upload New Document
              </Button>
            </div>
          ) : (
            <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2">
              {/* Found documents message */}
              <div className="flex items-center justify-between mb-4">
                <p className="text-sm text-emerald-400">
                  Found {documents.length} existing {roleLabel.toLowerCase()} document(s)
                </p>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={fetchExistingDocuments}
                  className="text-[#7fb89a] hover:text-emerald-400"
                >
                  <RefreshCw className="w-4 h-4 mr-1" />
                  Refresh
                </Button>
              </div>

              {/* Document cards */}
              {documents.map((doc) => (
                <DocumentCard
                  key={doc.document_id}
                  doc={doc}
                  isSelected={selectedDoc?.document_id === doc.document_id}
                  onSelect={() => setSelectedDoc(doc)}
                  onPreview={() => setPreviewDoc(doc)}
                  onUse={() => handleUseDocument(doc)}
                  submitting={submitting}
                />
              ))}
            </div>
          )}
        </div>

        <DialogFooter className="flex flex-col sm:flex-row gap-2 border-t border-emerald-900/30 pt-4">
          <Button
            variant="outline"
            onClick={onUploadNew}
            className="border-emerald-700/50 text-[#7fb89a] hover:bg-emerald-900/30"
          >
            <Upload className="w-4 h-4 mr-2" />
            Upload Different Document
          </Button>
          <Button
            variant="ghost"
            onClick={onClose}
            className="text-[#7fb89a]"
          >
            Cancel
          </Button>
        </DialogFooter>

        {/* Preview Modal */}
        {previewDoc && (
          <PreviewModal
            doc={previewDoc}
            onClose={() => setPreviewDoc(null)}
            onUse={() => {
              handleUseDocument(previewDoc);
              setPreviewDoc(null);
            }}
            submitting={submitting}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

/**
 * Individual document card
 */
const DocumentCard = ({ doc, isSelected, onSelect, onPreview, onUse, submitting }) => {
  const statusBadge = STATUS_BADGES[doc.status] || STATUS_BADGES.cropped_draft;
  
  const formatDate = (dateStr) => {
    if (!dateStr) return 'Unknown';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div
      className={`
        group relative flex items-start gap-4 p-4 rounded-xl border transition-all cursor-pointer
        ${isSelected 
          ? 'bg-emerald-900/30 border-emerald-500/50' 
          : 'bg-[#052116] border-emerald-900/30 hover:border-emerald-700/50'
        }
      `}
      onClick={onSelect}
      data-testid={`existing-doc-${doc.document_id}`}
    >
      {/* Thumbnail */}
      <div className="relative w-20 h-20 flex-shrink-0 rounded-lg overflow-hidden bg-[#031810] border border-emerald-900/30">
        {doc.thumbnail_url || doc.preview_url ? (
          <img
            src={doc.thumbnail_url || doc.preview_url}
            alt={doc.title}
            className="w-full h-full object-cover"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
        ) : null}
        <div className="absolute inset-0 flex items-center justify-center" style={{ display: doc.thumbnail_url || doc.preview_url ? 'none' : 'flex' }}>
          <Image className="w-8 h-8 text-emerald-700/50" />
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <h4 className="font-medium text-[#e8fef1] truncate mb-1">
          {doc.title || 'Untitled Document'}
        </h4>
        
        <div className="flex items-center gap-2 mb-2">
          <Badge className={`text-xs ${statusBadge.className}`}>
            {statusBadge.label}
          </Badge>
          {doc.is_attachment_ready && (
            <Badge className="text-xs bg-emerald-900/40 text-emerald-300 border border-emerald-600/30">
              Attachment Ready
            </Badge>
          )}
        </div>

        <div className="flex items-center gap-3 text-xs text-[#7fb89a]">
          <span className="flex items-center gap-1">
            <Calendar className="w-3 h-3" />
            {formatDate(doc.created_at)}
          </span>
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-col gap-2">
        <Button
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            onUse();
          }}
          disabled={submitting}
          className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs"
          data-testid={`use-doc-${doc.document_id}`}
        >
          {submitting ? (
            <Loader2 className="w-3 h-3 animate-spin" />
          ) : (
            <>
              <Check className="w-3 h-3 mr-1" />
              Use This
            </>
          )}
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={(e) => {
            e.stopPropagation();
            onPreview();
          }}
          className="border-emerald-700/50 text-[#7fb89a] hover:bg-emerald-900/30 text-xs"
          data-testid={`preview-doc-${doc.document_id}`}
        >
          <Eye className="w-3 h-3 mr-1" />
          Preview
        </Button>
      </div>

      {/* Selected indicator */}
      {isSelected && (
        <div className="absolute top-2 right-2 w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center">
          <Check className="w-3 h-3 text-white" />
        </div>
      )}
    </div>
  );
};

/**
 * Full-size preview modal
 */
const PreviewModal = ({ doc, onClose, onUse, submitting }) => {
  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl bg-[#041610] border border-emerald-900/30 text-[#e8fef1]">
        <DialogHeader>
          <DialogTitle className="text-[#e8fef1]">{doc.title}</DialogTitle>
        </DialogHeader>
        
        <div className="relative aspect-[4/3] bg-[#031810] rounded-lg overflow-hidden">
          <img
            src={doc.cropped_url || doc.preview_url || doc.file_url}
            alt={doc.title}
            className="w-full h-full object-contain"
          />
        </div>

        <DialogFooter className="flex gap-2">
          <Button
            variant="ghost"
            onClick={onClose}
            className="text-[#7fb89a]"
          >
            Close
          </Button>
          <Button
            onClick={onUse}
            disabled={submitting}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
          >
            {submitting ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <Check className="w-4 h-4 mr-2" />
            )}
            Use This Document
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Compact inline selector for attachment slots
 */
export const ExistingDocumentInlineSelector = ({
  attachmentRole,
  packageId,
  existingDocument,
  onSelect,
  onUploadNew,
  onRemove,
}) => {
  const [showPicker, setShowPicker] = useState(false);

  const RoleIcon = DOC_TYPE_ICONS[attachmentRole] || FileText;
  const roleLabel = ROLE_LABELS[attachmentRole] || attachmentRole?.replace(/_/g, ' ');

  if (existingDocument) {
    // Document already selected
    return (
      <div className="flex items-center gap-3 p-3 rounded-xl bg-emerald-900/20 border border-emerald-600/30">
        <div className="w-12 h-12 rounded-lg overflow-hidden bg-[#031810] flex-shrink-0">
          {existingDocument.cropped_url || existingDocument.file_url ? (
            <img
              src={existingDocument.cropped_url || existingDocument.file_url}
              alt={existingDocument.title}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <RoleIcon className="w-5 h-5 text-emerald-600" />
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-[#e8fef1] truncate">
            {existingDocument.title || roleLabel}
          </p>
          <p className="text-xs text-emerald-400">Attached</p>
        </div>
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setShowPicker(true)}
            className="text-[#7fb89a] hover:text-emerald-400"
          >
            Change
          </Button>
          {onRemove && (
            <Button
              size="sm"
              variant="ghost"
              onClick={onRemove}
              className="text-red-400 hover:text-red-300"
            >
              <X className="w-4 h-4" />
            </Button>
          )}
        </div>

        <ExistingDocumentPicker
          isOpen={showPicker}
          attachmentRole={attachmentRole}
          packageId={packageId}
          onSelect={(doc) => {
            onSelect?.(doc);
            setShowPicker(false);
          }}
          onUploadNew={() => {
            setShowPicker(false);
            onUploadNew?.();
          }}
          onClose={() => setShowPicker(false)}
        />
      </div>
    );
  }

  // No document selected yet
  return (
    <div className="border-2 border-dashed border-emerald-700/40 rounded-xl p-4 hover:border-emerald-500/50 transition-colors">
      <div className="flex flex-col items-center text-center gap-3">
        <div className="w-12 h-12 rounded-full bg-emerald-900/30 flex items-center justify-center">
          <RoleIcon className="w-6 h-6 text-emerald-500" />
        </div>
        <div>
          <p className="text-sm font-medium text-[#e8fef1]">{roleLabel}</p>
          <p className="text-xs text-[#7fb89a] mt-1">Required for mailing</p>
        </div>
        <div className="flex gap-2 mt-2">
          <Button
            size="sm"
            onClick={() => setShowPicker(true)}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid={`use-existing-${attachmentRole}`}
          >
            <FileText className="w-4 h-4 mr-1" />
            Use Existing
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={onUploadNew}
            className="border-emerald-700/50 text-[#7fb89a] hover:bg-emerald-900/30"
            data-testid={`upload-new-${attachmentRole}`}
          >
            <Upload className="w-4 h-4 mr-1" />
            Upload New
          </Button>
        </div>
      </div>

      <ExistingDocumentPicker
        isOpen={showPicker}
        attachmentRole={attachmentRole}
        packageId={packageId}
        onSelect={(doc) => {
          onSelect?.(doc);
          setShowPicker(false);
        }}
        onUploadNew={() => {
          setShowPicker(false);
          onUploadNew?.();
        }}
        onClose={() => setShowPicker(false)}
      />
    </div>
  );
};

export default ExistingDocumentPicker;
